import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';

import { GOOGLE_CALENDER_SERVICE, IGoogleCalenderService } from '@data-access/google';
import { AUTH_SERVICE, IAuthService } from '@data-access/repository';
import { INIT_ACCESS_TOKEN, LOGOUT_ACCESS_TOKEN, ORGANIZATION_ID, SKIP_HEADER } from '@data-access/utils';

import { LOCAL_STORAGE, ToastMessages } from '@shared/constants';
import { ILoggerService, IToasterService, LOGGER_SERVICE, TOASTER_SERVICE } from '@shared/services';
import { IUserStateService, USER_STATE_SERVICE } from '@shared/state';
import { GLOBAL, storageManager } from '@shared/storage';
import { readAccountList } from '@shared/utils';

import { API_RESPONSE_STATUS } from '../constants/api-response-status.constants';
import {
  ERROR_EXCEPTION_CODES,
  HttpStatusCode,
  InterceptorToastMessages,
  INVALID_API_TOKEN_ERROR_MESSAGE
} from '../constants/error.constants';

let refreshTokenInProgress$: Observable<string> | null = null;

export const authInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject<IAuthService>(AUTH_SERVICE);
  const loggerService = inject<ILoggerService>(LOGGER_SERVICE);
  const toasterService = inject<IToasterService>(TOASTER_SERVICE);
  const userStateService = inject<IUserStateService>(USER_STATE_SERVICE);

  const googleService = inject<IGoogleCalenderService>(GOOGLE_CALENDER_SERVICE);
  const router = inject(Router);

  const initToken = request.context.get(INIT_ACCESS_TOKEN) || null;
  const logoutAccessToken = request.context.get(LOGOUT_ACCESS_TOKEN) || null;
  const accountAccessToken = storageManager.local.get<string>(LOCAL_STORAGE.ACCESS_TOKEN);
  const activeUserId = userStateService.getActiveUserId();

  const orgId = request.context.get(ORGANIZATION_ID) ?? userStateService.getActiveOrgId();
  const accessToken = initToken ?? logoutAccessToken ?? accountAccessToken ?? null;
  const skipHeader = request.context.get(SKIP_HEADER);

  if (!skipHeader && accessToken) {
    request = addTokenHeader(request, accessToken, orgId);
  }
  return next(request).pipe(
    catchError(error => {
      if (error?.status === HttpStatusCode.FORBIDDEN && !accessToken && !skipHeader) {
        redirectAfterAccountLogout(router, userStateService);
      } else if (
        error?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.error?.exception_code === ERROR_EXCEPTION_CODES.INVALID_OR_EXPIRED_TOKEN
      ) {
        userStateService.clearActiveUserId();
        storageManager.local.clear();
        storageManager.session.clear();

        if (!router.url.includes('/auth/login')) {
          toasterService.error(ToastMessages.SessionExpired);
          router.navigate(['auth/login']);
        }
      } else if (
        error?.status === HttpStatusCode.FORBIDDEN &&
        error?.error?.message === INVALID_API_TOKEN_ERROR_MESSAGE &&
        !request.url.includes('token/refresh')
      ) {
        return handleRefreshToken(request, next, authService, router, toasterService, userStateService);
      } else if (
        error?.error?.exception_code === 'USER006' &&
        (request as { body?: { userInitiated?: boolean } })?.body?.userInitiated
      ) {
        return googleService.getRedirectionUrl().pipe(
          switchMap(res => {
            if (res.status === API_RESPONSE_STATUS.SUCCESS) {
              storageManager.local.set(LOCAL_STORAGE.LAST_URL_BEFORE_REDIRECT, router.url, GLOBAL);
              window.location.href = res.data;
              return EMPTY;
            }
            return throwError(() => error);
          }),
          catchError(err => {
            loggerService.error(InterceptorToastMessages.ErrorWhileGettingRedirectionUrl, err);
            return throwError(() => err);
          })
        );
      }
      return throwError(() => error);
    })
  );
};

const handleRefreshToken = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: IAuthService,
  router: Router,
  toasterService: IToasterService,
  userStateService: IUserStateService
) => {
  if (!refreshTokenInProgress$) {
    const refreshToken = storageManager.local.get<string>(LOCAL_STORAGE.REFRESH_TOKEN);
    if (!refreshToken) {
      toasterService.error(ToastMessages.SessionExpired);
      userStateService.clearActiveUserId();
      storageManager.local.clear();
      storageManager.session.clear();
      router.navigate(['auth/login']);
      return throwError(() => new Error('Missing refresh token'));
    }

    refreshTokenInProgress$ = authService.refreshToken(refreshToken).pipe(
      switchMap(response => {
        if (response.isSuccess()) {
          const data = response.value.getValue();
          storageManager.local.set(LOCAL_STORAGE.ACCESS_TOKEN, data.access_token);
          storageManager.local.set(LOCAL_STORAGE.REFRESH_TOKEN, data.refresh_token);
          return of(data.access_token);
        }
        throw new Error('Token refresh failed');
      }),
      catchError(error => {
        toasterService.error(ToastMessages.SessionExpired);
        userStateService.clearActiveUserId();
        storageManager.local.clear();
        storageManager.session.clear();
        router.navigate(['auth/login']);
        return throwError(() => error);
      }),
      finalize(() => {
        refreshTokenInProgress$ = null;
      }),
      shareReplay(1)
    );
  }
  return refreshTokenInProgress$.pipe(
    switchMap(token => {
      const orgId = request.context.get(ORGANIZATION_ID) ?? userStateService.getActiveOrgId();
      return next(addTokenHeader(request, token, orgId));
    })
  );
};

const addTokenHeader = (
  request: HttpRequest<unknown>,
  accessToken: string,
  orgId?: number | string | null
) => {
  let headers = request.headers.set('x-api-token', accessToken);

  if (orgId !== null && orgId !== undefined && orgId !== '') {
    headers = headers.set('x-org-id', `${orgId}`);
  }

  return request.clone({ headers });
};

const redirectAfterAccountLogout = (router: Router, userStateService: IUserStateService): void => {
  const hasRemainingAccounts = readAccountList().length > 0;
  userStateService.clearActiveUserId();

  if (hasRemainingAccounts) {
    router.navigate(['/account-dashboard']);
    return;
  }
  storageManager.local.clear();
  storageManager.session.clear();
  router.navigate(['auth/login']);
};
